import React, { useState, useContext, useRef, useCallback } from "react";
import {
  FaReply,
  FaEdit,
  FaTrash,
  FaThumbtack,
  FaFlag,
  FaEllipsisV,
} from "react-icons/fa";
import { UserContext } from "../../../../context/UserContext";
import { useRoomMessages } from "../../../../context/RoomMessagesContext";
import MessageReactions from "./MessageReactions";
import PinMessageModal from "./PinMessageModal";
import ReportMessageModal from "./ReportMessageModal";
import DeleteMessageModal from "./DeleteMessageModal";
import "./MessageComponent.css";

/**
 * Get initials from name or username
 */
const getInitials = (name, username) => {
  if (name) {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (username) {
    return username.substring(0, 2).toUpperCase();
  }
  return "??";
};

/**
 * Get consistent avatar color based on name
 */
const getAvatarColor = (name) => {
  const colors = [
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#f59e0b",
    "#ec4899",
    "#06b6d4",
    "#6366f1",
    "#14b8a6",
  ];
  const charCode = (name || "U").charCodeAt(0);
  return colors[charCode % colors.length];
};

/**
| * Check if user can edit message (15 minute window)
| */
const canEditMessage = (message, currentUserId) => {
  const isAuthor =
    message.sender?._id === currentUserId || message.sender === currentUserId;
  const editWindow = 15 * 60 * 1000; // 15 minutes
  const timeSince = Date.now() - new Date(message.createdAt).getTime();

  return isAuthor && timeSince < editWindow && !message.isDeleted;
};

/**
| * Check if user can delete message
| */
const canDeleteMessage = (message, currentUserId, isRoomLeader) => {
  const isAuthor =
    message.sender?._id === currentUserId || message.sender === currentUserId;
  const deleteWindow = 24 * 60 * 60 * 1000; // 24 hours
  const timeSince = Date.now() - new Date(message.createdAt).getTime();

  return (
    (isRoomLeader || (isAuthor && timeSince < deleteWindow)) &&
    !message.isDeleted
  );
};

/**
| * Format timestamp to readable format
| */
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
| * Parse and highlight @mentions
| */
const highlightMentions = (content) => {
  if (!content) return "";

  return content.split(/(@\w+)/g).map((part, index) => {
    if (part.startsWith("@")) {
      const username = part.substring(1);
      const isAll =
        username.toLowerCase() === "all" ||
        username.toLowerCase() === "everyone";
      return (
        <span key={index} className={`mention ${isAll ? "mention-all" : ""}`}>
          {part}
        </span>
      );
    }
    return part;
  });
};

const MessageComponent = ({
  message,
  roomId,
  userRole,
  onReply,
  onJumpToMessage,
  isHighlighted,
}) => {
  const { user } = useContext(UserContext);
  const {
    editMessage,
    deleteMessage,
    pinMessage,
    unpinMessage,
    reportMessage,
  } = useRoomMessages();

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content || "");
  const [showActions, setShowActions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactions, setShowReactions] = useState(false);

  // Swipe to reply state
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const swipeThreshold = 80; // pixels to trigger reply
  const messageRef = useRef(null);

  // Long press detection for mobile
  const longPressTimer = useRef(null);

  // Handle touch start - track initial position
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    setIsSwiping(false);

    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      setShowReactions(true);
    }, 500);
  }, []);

  // Handle touch move - detect swipe
  const handleTouchMove = useCallback(
    (e) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;

      // Only allow horizontal swipe (not vertical scroll)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        setIsSwiping(true);

        // For own messages, swipe left (negative). For others, swipe right (positive)
        const isOwnMsg =
          message.sender?._id === (user?.id || user?._id) ||
          message.sender === (user?.id || user?._id);

        if (isOwnMsg) {
          // Own message: swipe left only (negative values)
          const swipe = Math.min(0, Math.max(deltaX, -swipeThreshold - 20));
          setSwipeX(swipe);
        } else {
          // Other's message: swipe right only (positive values)
          const swipe = Math.max(0, Math.min(deltaX, swipeThreshold + 20));
          setSwipeX(swipe);
        }

        // Prevent vertical scroll while swiping
        if (Math.abs(deltaX) > 20) {
          e.preventDefault();
        }
      }
    },
    [message.sender, user]
  );

  // Handle touch end - trigger reply if threshold reached
  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    // Check if swipe threshold was reached
    if (Math.abs(swipeX) >= swipeThreshold && onReply && !message.isDeleted) {
      // Trigger haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      onReply();
    }

    // Animate back to original position
    setSwipeX(0);
    setIsSwiping(false);
  }, [swipeX, swipeThreshold, onReply, message.isDeleted]);

  // Hide reactions when clicking elsewhere
  const handleMessageClick = useCallback(() => {
    if (showReactions) {
      setShowReactions(false);
    }
  }, [showReactions]);

  const currentUserId = user?.id || user?._id;
  const isOwnMessage =
    message.sender?._id === currentUserId || message.sender === currentUserId;
  const canEdit = canEditMessage(message, currentUserId);
  const canDelete = canDeleteMessage(
    message,
    currentUserId,
    userRole?.isLeader
  );

  // Handle deleted messages - show who deleted it
  if (message.isDeleted) {
    const deleterName = message.deletedByName || "Someone";
    return (
      <div className="message message-deleted">
        <p className="deleted-text">
          <FaTrash /> {deleterName} deleted this message
        </p>
      </div>
    );
  }

  // Handle message edit
  const handleEdit = async () => {
    if (!editContent.trim() || editContent === message.content) {
      setIsEditing(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await editMessage(roomId, message._id, editContent);
      setIsEditing(false);
    } catch (error) { /* ignored */ } finally {
      setIsSubmitting(false);
    }
  };

  // Handle message delete
  const handleDelete = async () => {
    setShowActions(false);
    setIsSubmitting(true);
    try {
      await deleteMessage(roomId, message._id);
    } catch (error) {
alert("Failed to delete message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle pin/unpin
  const handlePin = async () => {
    setShowActions(false);
    try {
      if (message.isPinned) {
        await unpinMessage(roomId, message._id);
      } else {
        await pinMessage(roomId, message._id);
      }
    } catch (error) { /* ignored */ }
  };

  // Handle report
  const handleReport = async (reportData) => {
    try {
      await reportMessage(roomId, message._id, reportData);
      alert("Message reported successfully. Our team will review it.");
    } catch (error) {
alert("Failed to report message. Please try again.");
    }
  };

  const senderName = message.sender?.name || "Unknown User";
  const senderUsername = message.sender?.username;
  const senderPhoto = message.sender?.profilePhotoUrl;
  const hasValidPhoto = senderPhoto && !senderPhoto.includes("default-avatar");
  const isVerified = message.sender?.isVerified || false;

  // Swipe progress for visual feedback (0 to 1)
  const swipeProgress = Math.min(Math.abs(swipeX) / swipeThreshold, 1);

  return (
    <div
      ref={messageRef}
      className={`message ${isOwnMessage ? "message-own" : ""} ${
        message.isPinned ? "message-pinned" : ""
      } ${showReactions ? "show-reactions" : ""} ${
        isHighlighted ? "message-highlighted" : ""
      } ${isSwiping ? "message-swiping" : ""}`}
      data-message-id={message._id}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onClick={handleMessageClick}
      style={{
        transform: swipeX !== 0 ? `translateX(${swipeX}px)` : undefined,
        transition: swipeX === 0 ? "transform 0.2s ease-out" : "none",
      }}
    >
      {/* Swipe Reply Indicator - appears behind message when swiping */}
      {swipeX !== 0 && (
        <div
          className={`swipe-reply-indicator ${
            isOwnMessage ? "swipe-left" : "swipe-right"
          }`}
          style={{
            opacity: swipeProgress,
            transform: `scale(${0.5 + swipeProgress * 0.5})`,
          }}
        >
          <FaReply />
        </div>
      )}

      {/* Sender Avatar */}
      {!isOwnMessage && (
        <div className="message-avatar">
          {hasValidPhoto ? (
            <img src={senderPhoto} alt={senderName} />
          ) : (
            <div
              className="message-avatar-initials"
              style={{ backgroundColor: getAvatarColor(senderName) }}
            >
              {getInitials(senderName, senderUsername)}
            </div>
          )}
        </div>
      )}

      {/* Message Bubble */}
      <div className="message-bubble">
        {/* Reply Indicator - Clickable to jump to original message */}
        {message.replyTo && (
          <div
            className="reply-indicator reply-clickable"
            onClick={(e) => {
              e.stopPropagation();
              if (onJumpToMessage && message.replyTo._id) {
                onJumpToMessage(message.replyTo._id);
              }
            }}
            title="Click to jump to original message"
          >
            <FaReply size={9} />
            <span className="reply-sender-name">
              {message.replyTo.sender?.name || "a message"}
            </span>
            {message.replyTo.content && (
              <span className="reply-preview-text">
                {message.replyTo.content.substring(0, 50)}
                {message.replyTo.content.length > 50 ? "..." : ""}
              </span>
            )}
          </div>
        )}

        {/* Content or Edit Mode */}
        {isEditing ? (
          <div className="message-edit-mode">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              disabled={isSubmitting}
              className="edit-textarea"
              autoFocus
            />
            <div className="edit-actions">
              <button
                onClick={handleEdit}
                disabled={isSubmitting || !editContent.trim()}
                className="btn-save"
              >
                {isSubmitting ? "..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(message.content);
                }}
                disabled={isSubmitting}
                className="btn-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="message-content-wrapper">
              {/* Header Row: Name + Time + Actions on SAME LINE */}
              {!isOwnMessage && (
                <div className="message-header-row">
                  <span className="sender-name">
                    {senderName}
                    {isVerified && <span className="verified-badge">✓</span>}
                  </span>

                  <div className="header-meta">
                    <span className="message-time">
                      {formatTime(message.createdAt)}
                    </span>
                    {message.isEdited && (
                      <span className="edited-indicator">edited</span>
                    )}
                    {message.isPinned && (
                      <FaThumbtack
                        size={9}
                        className="pinned-icon"
                        title="Pinned"
                      />
                    )}

                    {/* Three-dot menu */}
                    <span className="action-more">
                      <button
                        onClick={() => setShowActions(!showActions)}
                        className="action-btn-dots"
                        title="More actions"
                      >
                        <FaEllipsisV size={11} />
                      </button>

                      {showActions && (
                        <>
                          <div
                            className="actions-backdrop"
                            onClick={() => setShowActions(false)}
                          />
                          <div className="actions-menu">
                            <button
                              onClick={() => {
                                onReply();
                                setShowActions(false);
                              }}
                              className="menu-item"
                            >
                              <FaReply /> Reply
                            </button>

                            {canEdit && (
                              <button
                                onClick={() => {
                                  setIsEditing(true);
                                  setShowActions(false);
                                }}
                                className="menu-item"
                                disabled={isSubmitting}
                              >
                                <FaEdit /> Edit
                              </button>
                            )}

                            {canDelete && (
                              <button
                                onClick={() => {
                                  setShowActions(false);
                                  setShowDeleteModal(true);
                                }}
                                className="menu-item menu-item-danger"
                                disabled={isSubmitting}
                              >
                                <FaTrash /> Delete
                              </button>
                            )}

                            {userRole?.isLeader && !message.isPinned && (
                              <button
                                className="menu-item"
                                onClick={() => {
                                  setShowActions(false);
                                  setShowPinModal(true);
                                }}
                              >
                                <FaThumbtack /> Pin
                              </button>
                            )}
                            {userRole?.isLeader && message.isPinned && (
                              <button
                                className="menu-item"
                                onClick={() => {
                                  setShowActions(false);
                                  setShowPinModal(true);
                                }}
                              >
                                <FaThumbtack /> Unpin
                              </button>
                            )}
                            <button
                              className="menu-item"
                              onClick={() => {
                                setShowActions(false);
                                setShowReportModal(true);
                              }}
                            >
                              <FaFlag /> Report
                            </button>
                          </div>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              )}{" "}
              {/* Own message header */}
              {isOwnMessage && (
                <div className="message-header-row message-header-own">
                  <div className="header-meta">
                    <span className="message-time">
                      {formatTime(message.createdAt)}
                    </span>
                    {message.isEdited && (
                      <span className="edited-indicator">edited</span>
                    )}
                    {message.isPinned && (
                      <FaThumbtack
                        size={9}
                        className="pinned-icon"
                        title="Pinned"
                      />
                    )}

                    {/* Three-dot menu */}
                    <span className="action-more">
                      <button
                        onClick={() => setShowActions(!showActions)}
                        className="action-btn-dots"
                        title="More actions"
                      >
                        <FaEllipsisV size={11} />
                      </button>

                      {showActions && (
                        <>
                          <div
                            className="actions-backdrop"
                            onClick={() => setShowActions(false)}
                          />
                          <div className="actions-menu">
                            <button
                              onClick={() => {
                                onReply();
                                setShowActions(false);
                              }}
                              className="menu-item"
                            >
                              <FaReply /> Reply
                            </button>

                            {canEdit && (
                              <button
                                onClick={() => {
                                  setIsEditing(true);
                                  setShowActions(false);
                                }}
                                className="menu-item"
                                disabled={isSubmitting}
                              >
                                <FaEdit /> Edit
                              </button>
                            )}

                            {canDelete && (
                              <button
                                onClick={() => {
                                  setShowActions(false);
                                  setShowDeleteModal(true);
                                }}
                                className="menu-item menu-item-danger"
                                disabled={isSubmitting}
                              >
                                <FaTrash /> Delete
                              </button>
                            )}

                            {userRole?.isLeader && !message.isPinned && (
                              <button
                                className="menu-item"
                                onClick={() => {
                                  setShowActions(false);
                                  setShowPinModal(true);
                                }}
                              >
                                <FaThumbtack /> Pin
                              </button>
                            )}
                            {userRole?.isLeader && message.isPinned && (
                              <button
                                className="menu-item"
                                onClick={() => {
                                  setShowActions(false);
                                  setShowPinModal(true);
                                }}
                              >
                                <FaThumbtack /> Unpin
                              </button>
                            )}
                            <button
                              className="menu-item"
                              onClick={() => {
                                setShowActions(false);
                                setShowReportModal(true);
                              }}
                            >
                              <FaFlag /> Report
                            </button>
                          </div>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              )}
              {/* Content on SEPARATE LINE */}
              <div className="message-text">
                {highlightMentions(message.content)}
              </div>
              {/* Flag Warning */}
              {message.isFlagged && (
                <div className="message-warning">
                  <FaFlag size={9} /> {message.flagReason}
                </div>
              )}
            </div>
          </>
        )}

        {/* WhatsApp-style Reactions */}
        <MessageReactions
          message={message}
          roomId={roomId}
          isOwnMessage={isOwnMessage}
        />
      </div>

      {/* Modals */}
      {showPinModal && (
        <PinMessageModal
          message={message}
          isUnpin={message.isPinned}
          onConfirm={handlePin}
          onClose={() => setShowPinModal(false)}
        />
      )}

      {showReportModal && (
        <ReportMessageModal
          message={message}
          onConfirm={handleReport}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {showDeleteModal && (
        <DeleteMessageModal
          message={message}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default MessageComponent;
